import { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from '../dialogs/dialog';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { ListCourseDto, PurchaseDto, PurchaseQuoteDto } from '../../general/dtos';
import { formatCoursePrice } from '../../general/currency';
import { openCourseCheckout, releaseCourseCheckout } from '../../general/paddle';
import { useApi } from '../../providers/api-provider';
import { useAuth } from '../../providers/auth-provider';
import { GeneralInfoDialog } from '../dialogs/info-dialog';
import { PolicyContent } from '../policy-content';
import { policies } from '../../content/policies';
import '../../assets/catalogue.css';
import '../../assets/theme-content.css';

export const pendingCourseStorage = 'course-to-buy';
export function purchaseError(error: any): string {
    return error?.response?.data?.message || error?.message || 'Could not complete this request. Please try again.';
}

export function CoursePurchase({ course, onHide, onPurchased }: {
    course: ListCourseDto | null; onHide: () => void; onPurchased: () => void;
}) {
    const api = useApi();
    const auth = useAuth();
    const authenticated = auth.getIsAuthenticated();
    const sub = auth.getUser?.()?.sub ?? '';
    const [quote, setQuote] = useState<PurchaseQuoteDto | null>(null);
    const [purchase, setPurchase] = useState<PurchaseDto | null>(null);
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [polling, setPolling] = useState(false);
    const [restarting, setRestarting] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [policy, setPolicy] = useState<keyof typeof policies | null>(null);
    const mounted = useRef(true);
    const request = useRef(0);
    const completedKey = useRef('');
    const visible = !!course || !!purchase;
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; releaseCourseCheckout(); };
    }, []);

    useEffect(() => {
        if (!course || !api) return;
        let active = true;
        const version = ++request.current;
        setPurchase(null); setCode(''); setQuote(null); setError(''); setNotice(''); setBusy(true); setAgreed(false); setPolicy(null); setPolling(false); setRestarting(false);
        api.prepareCoursePurchase(course.course_key, '').then(result => {
            if (active && mounted.current && request.current === version) {
                setQuote(result.quote); setPurchase(result.purchase);
                setRestarting(!!result.purchase); setPolling(!!result.purchase);
            }
        }).catch(err => {
            if (active && mounted.current && request.current === version) setError(purchaseError(err));
        }).finally(() => { if (active && mounted.current && request.current === version) setBusy(false); });
        return () => { active = false; };
    }, [course, api]);

    useEffect(() => { setAgreed(false); }, [authenticated, sub]);

    useEffect(() => {
        if (!purchase?.fulfilled_at) return;
        setPolling(false);
        if (completedKey.current !== purchase.purchase_key) {
            completedKey.current = purchase.purchase_key;
            onPurchased();
        }
    }, [purchase, onPurchased]);

    useEffect(() => {
        if (!api || !purchase || !polling || purchase.fulfilled_at) return;
        let active = true;
        let timer: ReturnType<typeof setTimeout>;
        const until = Date.now() + 120000;
        const poll = async () => {
            try {
                const next = await api.getCoursePurchase(purchase.purchase_key);
                if (!active) return;
                setPurchase(next);
                if (next.fulfilled_at) { setPolling(false); return; }
                if (restarting || ['canceled', 'creation_failed'].includes(next.status)) {
                    const prepared = await api.prepareCoursePurchase(next.course_key, '');
                    if (!active) return;
                    setPurchase(prepared.purchase); setQuote(prepared.quote);
                    if (!prepared.purchase) {
                        setRestarting(false); setPolling(false); setNotice(''); setCode(''); return;
                    }
                }
            } catch { /* Keep confirmation pending during temporary API failures. */ }
            if (!active) return;
            if (Date.now() < until) timer = setTimeout(poll, 2000);
            else { setPolling(false); setNotice('Your payment is taking longer to confirm. You can close this window and return later.'); }
        };
        timer = setTimeout(poll, 2000);
        return () => { active = false; clearTimeout(timer); };
    }, [api, purchase?.purchase_key, polling, purchase?.fulfilled_at, restarting]); // eslint-disable-line react-hooks/exhaustive-deps

    const applyCode = async () => {
        if (!api || !course) return;
        setBusy(true); setError(''); setQuote(null);
        const version = ++request.current;
        try {
            const result = await api.quoteCoursePurchase(course.course_key, code);
            if (mounted.current && request.current === version) setQuote(result);
        } catch (err) { if (mounted.current && request.current === version) setError(purchaseError(err)); }
        finally { if (mounted.current && request.current === version) setBusy(false); }
    };

    const launch = async (result: PurchaseDto) => {
        if (!api) return;
        if (!result.checkout_created || !result.paddle_transaction_id || result.status !== 'pending') {
            setRestarting(true); setPolling(true); return;
        }
        const config = await api.getPaymentConfig();
        let completed = false;
        setRestarting(false); setPolling(true);
        await openCourseCheckout(config, result.paddle_transaction_id, name => {
            if (!mounted.current) return;
            if (name === 'checkout.completed') {
                completed = true; setRestarting(false); setPolling(true);
            } else if (name === 'checkout.closed' && !completed) {
                setPurchase(null); setPolling(false); setRestarting(false);
                setNotice('Payment was not completed. You can try again.');
            }
        });
    };

    const buy = async () => {
        if (!api || !quote || !authenticated || busy || !agreed) return;
        const version = ++request.current;
        setBusy(true); setError(''); setNotice('');
        try {
            const prepared = await api.prepareCoursePurchase(quote.course_key, quote.promo_code ?? '');
            if (!mounted.current || request.current !== version) return;
            setPurchase(prepared.purchase); setQuote(prepared.quote);
            if (prepared.purchase) { setRestarting(true); setPolling(true); return; }
            if (prepared.quote.payable_amount_minor !== quote.payable_amount_minor
                || prepared.quote.quoted_amount_minor !== quote.quoted_amount_minor
                || prepared.quote.promo_code !== quote.promo_code) {
                setNotice('The price has changed. Please review the total before continuing.'); return;
            }
            const result = await api.createCourseCheckout(quote.course_key, quote.promo_code ?? '', prepared.quote);
            if (!mounted.current || request.current !== version) return;
            sessionStorage.removeItem(pendingCourseStorage);
            setPurchase(result); setQuote(result);
            if (!result.fulfilled_at) await launch(result);
        } catch (err) {
            if (mounted.current && request.current === version) {
                setPurchase(null); setQuote(null); setPolling(false); setError(purchaseError(err));
            }
        } finally { if (mounted.current && request.current === version) setBusy(false); }
    };

    const hide = () => {
        request.current++; releaseCourseCheckout(); setPolling(false); setRestarting(false); setPurchase(null); setQuote(null); setAgreed(false); setPolicy(null); onHide();
    };
    return <><Dialog header={purchase?.fulfilled_at ? 'Purchase confirmed' : 'Buy a product'} visible={visible} modal className="theme-content course-purchase-dialog"
        style={{ width: '32rem', maxWidth: '95vw' }} closable={!busy} onHide={hide}>
        <h2 className="mt-0">{quote?.course_name ?? course?.name}</h2>
        {purchase?.fulfilled_at ? <>
            <p>Your course is now available. Your access has no expiration.</p>
            <Button label="Go to home" icon="pi pi-home" onClick={() => window.location.assign('/')} />
        </> : <>
            {!purchase && <div className="flex flex-column gap-2">
                <label htmlFor="course-promo" className="theme-field-label">Discount code (optional)</label>
                <div className="flex gap-2 course-promo-controls">
                    <InputText id="course-promo" value={code} maxLength={32} disabled={busy} className="course-promo-input"
                        onChange={e => { setCode(e.target.value); setQuote(null); }} />
                    <Button label="Apply" outlined disabled={busy} className="course-promo-apply" onClick={applyCode} />
                </div>
            </div>}
            {quote && !purchase && <div className="my-3">
                <p>Course price: {formatCoursePrice(quote.quoted_amount_minor / 100)}</p>
                {quote.loyalty_discount_percent && <p>Loyalty discount: {quote.loyalty_discount_percent}%{quote.loyalty_course_name ? ` (purchased ${quote.loyalty_course_name})` : ''}</p>}
                {quote.promo_code && <p>Promo code ({quote.promo_code}): {quote.promo_discount_percent ?? quote.discount_percent}%</p>}
                {quote.discount_amount_minor > 0 && <p>Total discount ({quote.discount_percent}%): −{formatCoursePrice(quote.discount_amount_minor / 100)}</p>}
                <p className="font-semibold">Total: {formatCoursePrice(quote.payable_amount_minor / 100)}</p>
                <small>Includes applicable taxes. One-time payment.</small>
            </div>}
            {notice && <p role="status">{notice}</p>}
            {error && <p role="alert">{error}</p>}
            {purchase ? !notice && <p role="status">We're confirming your payment. Please wait.</p> : <>
                    <div className="flex align-items-start gap-2 my-3">
                        <Checkbox inputId="course-policy-agreement" checked={agreed} disabled={busy}
                            onChange={event => setAgreed(!!event.checked)} />
                        <label htmlFor="course-policy-agreement" className="line-height-3">
                            I agree to the <a href="/terms-of-use"
                                onClick={event => { event.preventDefault(); event.stopPropagation(); setPolicy('terms'); }}>Terms & conditions</a> and <a href="/refund-policy"
                                onClick={event => { event.preventDefault(); event.stopPropagation(); setPolicy('refunds'); }}>Refund & cancellation policy</a>
                        </label>
                    </div>
                    <Button label="Continue to payment" icon="pi pi-credit-card"
                        loading={busy} disabled={!quote || !authenticated || polling || busy || !agreed} onClick={buy} />
                </>}
        </>}
    </Dialog>
        <GeneralInfoDialog visible={!!policy && visible} title={policy ? policies[policy].title : ''} message=""
            width="48rem" onClose={() => setPolicy(null)}>
            {policy && <PolicyContent policy={policy} />}
        </GeneralInfoDialog>
    </>;
}
